import { Link, useNavigate } from "react-router-dom";
import { Award, Cake, ShieldBan, Heart, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { imgSrc } from "@/lib/api";
import { useIsBirthday } from "@/lib/birthdays";
import { useIsVip } from "@/lib/vip";
import { useStoryRing } from "@/lib/storyRings";
import { useHonorTitle, useHonorTier, TIERS } from "@/lib/honor";
import { HonorBadge } from "@/components/HonorBadge";
import { FounderBadge, useIsFounder } from "@/components/FounderBadge";
import { AdminTeamBadge } from "@/components/AdminTeamBadge";
import { useLevelFrame } from "@/lib/levelFrames";

export const UserAvatar = ({ name, picture, size = "w-10 h-10", text = "text-sm", userId, crown = true, noStory = false }) => {
  const bday = useIsBirthday(userId);
  const honor = Boolean(useHonorTitle(userId));
  const tier = useHonorTier(userId);
  const TierIcon = tier ? TIERS[tier].icon : Award;
  const vip = useIsVip(userId) && !honor;
  const ring = useStoryRing(userId);
  const story = noStory ? null : ring;
  const frame = useLevelFrame(userId);
  const founder = useIsFounder(userId);
  const inner = (
    <div className={`${story ? "w-full h-full" : size} relative rounded-full shrink-0 ${bday ? "birthday-ring p-[3px]" : founder ? "founder-ring p-[3px]" : honor ? `tier-${tier} honor-ring p-[3px]` : vip ? "vip-ring p-[3px]" : frame ? `lvl-frame lvl-frame-${frame} p-[3px]` : story ? "bg-white p-0.5" : "pink-gradient p-0.5"}`} data-frame={!bday && !honor && !vip && frame ? frame : undefined} data-testid={bday ? `birthday-frame-${userId}` : honor ? `honor-frame-${userId}` : vip ? `vip-frame-${userId}` : undefined} title={bday ? `${name} hat heute Geburtstag` : honor ? `${name} ist Ehrenmitglied` : vip ? `${name} ist Projekt Unterstützer` : undefined}>
      {picture ? <img src={imgSrc(picture)} alt={name} className="w-full h-full rounded-full object-cover bg-white" />
        : <div className={`w-full h-full rounded-full bg-white flex items-center justify-center font-display font-extrabold text-rose-500 ${text}`}>{name?.[0]?.toUpperCase()}</div>}
      {vip && !bday && !founder && crown && <span className="absolute -top-[18%] -right-[12%] w-[46%] h-[46%] min-w-3.5 min-h-3.5 max-w-11 max-h-11 rotate-[18deg] drop-shadow pointer-events-none" aria-hidden><Heart className="w-full h-full text-rose-500 stroke-white" fill="currentColor" strokeWidth={1.5} /></span>}
      {honor && !bday && !founder && crown && <span className={`tier-${tier} honor-star absolute -top-[14%] -right-[10%] w-[40%] h-[40%] min-w-3.5 min-h-3.5 max-w-10 max-h-10 pointer-events-none`} aria-hidden><TierIcon className="w-full h-full" strokeWidth={2.4} /></span>}
      {founder && !bday && crown && <span className="founder-crown absolute -top-[30%] left-1/2 -translate-x-1/2 w-[48%] h-[48%] min-w-3.5 min-h-3.5 max-w-12 max-h-12 pointer-events-none" aria-hidden data-testid={`founder-crown-${userId}`}><Crown className="w-full h-full" fill="currentColor" strokeWidth={1.5} /></span>}
      {bday && <span className="absolute -bottom-0.5 -right-0.5 w-[40%] h-[40%] min-w-4 min-h-4 max-w-10 max-h-10 rounded-full bg-white shadow flex items-center justify-center"><Cake className="w-[65%] h-[65%] text-rose-500" /></span>}
    </div>
  );
  if (!story) return inner;
  const open = (e) => { e.preventDefault(); e.stopPropagation(); story.open(); };
  return (
    <span role="button" tabIndex={0} onClick={open} onKeyDown={(e) => e.key === "Enter" && open(e)} aria-label={`Story von ${name} ansehen`} data-testid={`story-ring-${userId}`} data-seen={story.seen ? "true" : "false"}
      className={`${size} block shrink-0 rounded-full p-[2.5px] cursor-pointer ${story.seen ? "bg-slate-300 ring-1 ring-slate-400" : "story-ring"}`}>
      {inner}
    </span>
  );
};

export const UserLink = ({ id, children, className = "", testId }) => {
  const honor = Boolean(useHonorTitle(id)) && typeof children === "string";
  const linkTier = useHonorTier(id);
  const vip = useIsVip(id) && typeof children === "string" && !honor;
  const { user } = useAuth();
  const navigate = useNavigate();
  const manage = user?.role === "admin" && id && id !== user.user_id && id !== "community";
  return (
    <>
      <Link to={`/u/${id}`} data-testid={testId || `user-link-${id}`} data-vip={vip || undefined} className={`${honor ? `tier-${linkTier} honor-name` : vip ? "vip-name" : "hover:text-rose-600"} hover:underline underline-offset-2 transition-colors ${className}`}>
        {children}
      </Link>
      {honor && <> <HonorBadge userId={id} testId={`honor-badge-link-${id}`} /></>}
      {typeof children === "string" && <FounderBadge userId={id} testId={`founder-badge-link-${id}`} />}
      {typeof children === "string" && <AdminTeamBadge userId={id} />}
      {manage && (
        <button type="button" title="Mitglied ausschließen / Punkte sperren" aria-label="Mitglied verwalten" data-testid={`admin-manage-${id}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/admin/mitglied/${id}`); }}
          className="inline-flex items-center justify-center align-middle ml-1 w-5 h-5 rounded-full bg-slate-100 text-slate-500 hover:bg-rose-100 hover:text-rose-600 transition-colors">
          <ShieldBan className="w-3 h-3" />
        </button>
      )}
    </>
  );
};
